<template>
    <div class="filters-bar">
        <slot name="scope" />
        <div v-if="$slots.scope" class="filters-sep" />

        <template v-if="!isStudent">
            <div v-if="isGroupOption" class="filter-group">
                <span class="filter-label">{{ $t('common.group') }}</span>
                <div class="filter-control filter-control--wide">
                    <kimsa-combobox :placeholder="$t('statistics.select_group_placeholder')" dense solo :items="groups"
                        @change="currentGroup = $event" :value="currentGroup" placeholder-class icon="$account_multiple"
                        searchable item-value="id" />
                </div>
            </div>
            <div v-else-if="!isTeamOption" class="filter-group">
                <span class="filter-label">{{ $t('common.student') }}</span>
                <div class="filter-control filter-control--wide">
                    <kimsa-combobox :placeholder="$t('statistics.select_student_placeholder')" dense solo
                        :items="students" @change="currentStudent = $event" :value="currentStudent" placeholder-class
                        icon="$account" searchable item-value="objectID" />
                </div>
            </div>
            <template v-else-if="isInjury || isWellness || isCompetence">
                <div class="filter-group">
                    <span class="filter-label">{{ $t('common.team') }}</span>
                    <div class="filter-control filter-control--wide">
                        <kimsa-combobox :placeholder="$t('statistics.select_team_placeholder')" dense solo :items="teams"
                            @change="currentTeam = $event" :value="currentTeam" placeholder-class
                            icon="$account_multiple" searchable item-value="objectID" />
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="filter-group">
                    <span class="filter-label">{{ $t('common.team') }}</span>
                    <div class="filter-control">
                        <kimsa-combobox :placeholder="$t('statistics.select_team_placeholder')" dense solo :items="teams"
                            @change="currentTeam = $event" :value="currentTeam" placeholder-class
                            icon="$account_multiple" searchable item-value="objectID" />
                    </div>
                </div>
                <div class="filter-group">
                    <span class="filter-label">{{ $t('common.subgroup') }}</span>
                    <div class="filter-control">
                        <kimsa-combobox :placeholder="$t('common.all')" dense solo :items="subgroups"
                            @change="currentSubgroup = $event" :value="currentSubgroup" placeholder-class
                            icon="$account_multiple" item-value="id" :readonly="!currentTeam" clearable />
                    </div>
                </div>
            </template>

            <div v-if="showCompareSelect" class="filter-group">
                <span class="filter-label">{{ $t('statistics.compare') }}</span>
                <div class="filter-control">
                    <kimsa-combobox dense solo no-object :items="compareOptions" :value="compareMode"
                        @change="compareMode = $event" />
                </div>
            </div>
        </template>

        <div v-if="isWellness" class="filter-group">
            <span class="filter-label">{{ $t('common.date') }}</span>
            <div class="filter-control filter-control--date">
                <kimsa-date-picker solo flat dense color="secondary" @change="onWellnessDate" :value="endDate"
                    append-icon="$calendar" ref="wellnessDate" />
            </div>
        </div>
        <template v-else>
            <div class="filter-group">
                <span class="filter-label">{{ $t('common.from') }}</span>
                <div class="filter-control filter-control--date">
                    <kimsa-date-picker solo flat dense color="secondary" :max="endDate" @change="startDate = $event"
                        :value="startDate" append-icon="$calendar" ref="start" />
                </div>
            </div>
            <div class="filter-group">
                <span class="filter-label">{{ $t('statistics.to') }}</span>
                <div class="filter-control filter-control--date">
                    <kimsa-date-picker :disabled="!startDate" solo flat dense color="secondary" :min="startDate"
                        @change="endDate = $event" :value="endDate" append-icon="$calendar" ref="end" />
                </div>
            </div>
        </template>

        <v-btn class="load-btn" depressed :loading="loading" @click="loadDocs()" :disabled="disabled">
            {{ $t('statistics.load') }}
        </v-btn>

        <button-tooltip bottom :tooltip="$t('common.export')" class="ml-auto">
            <template #activator="{ on, attrs }">
                <v-btn v-on="on" v-bind="attrs" icon class="export-btn" :loading="loadingExport"
                    :disabled="disabled || docs.length === 0" @click="exportDocs">
                    <v-icon size="18">mdi-file-export-outline</v-icon>
                </v-btn>
            </template>
        </button-tooltip>

        <button-tooltip v-if="canShare" bottom :tooltip="$t('common.share_image')">
            <template #activator="{ on, attrs }">
                <v-btn v-on="on" v-bind="attrs" icon class="export-btn" :loading="loadingShare"
                    :disabled="disabled || docs.length === 0" @click="shareImage">
                    <v-icon size="18">mdi-share-variant-outline</v-icon>
                </v-btn>
            </template>
        </button-tooltip>
    </div>
</template>

<script>
import { UserIndex } from "@/utils/Algolia";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import { db } from "@/utils/Firebase";
import { saveWorkbook } from "@/utils/Export";
import { shareNodeAsImage } from "@/utils/ShareImage";
import { getMergedBlocks, uniqueDayRoutine, uniqueDayTeamRoutine } from "../../utils/Exercises";
import { fetchTeamAdvances, fetchGroupAdvances, getScopeDocs } from "../../utils/Statistics";
import { mapState } from 'vuex';
import { calculateWellnessPreInfo, sumWellnessPreInfo } from "../../utils/Routine";

export default {
    name: "StatisticsForm",
    components: { KimsaDatePicker, KimsaCombobox, ButtonTooltip },
    props: {
        currentOption: {
            type: String,
            default: null
        },
        isCompetence: {
            type: Boolean,
            default: false
        },
        isInjury: {
            type: Boolean,
            default: false
        },
        isWellness: {
            type: Boolean,
            default: false
        },
        withLastWeeks: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            allStudents: [],
            loadedStudentsCoachId: null,
            currentStudent: null,
            startDate: null,
            endDate: null,
            docs: [],
            loadingExport: false,
            loadingShare: false,
            rpeTable: [
                { name: 10, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 9.5, reps: [97, 93, 89, 86, 84, 82, 80, 77.5] },
                { name: 9, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 8.5, reps: [93, 89, 86, 84, 82, 80, 77.5, 74.5] },
                { name: 8, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 7.5, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 7, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            rirTable: [
                { name: 0, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 1, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 2, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 3, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 4, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            currentTeam: null,
            currentGroup: null,
            currentSubgroup: null,
            compareMode: 'none',
            rawDocs: [],
            rawLastWeeks: null,
            compareData: null,
            compareCache: { key: null, data: null },
            // Cache de los docs del equipo (fetch con todos los alumnos) para que
            // cambiar entre alumnos del mismo equipo/rango sea instantáneo.
            cache: { key: null, docs: null, lastWeeks: null },
        }
    },
    computed: {
        ...mapState(['teams', 'groups', 'libraryCoach']),
        canShare(vm = this) {
            return vm.accountType === 'coach' || vm.accountType === 'company'
        },
        coachId(vm = this) {
            return vm.isCompany ?
                vm.libraryCoach?.id || null :
                vm.currentUser?.uid
        },
        isStudent(vm = this) {
            return vm.account?.accountType === 'student'
        },
        studentId(vm = this) {
            return vm.isStudent ?
                vm.currentUser?.uid :
                vm.currentStudent?.objectID
        },
        studentName(vm = this) {
            return vm.isStudent ?
                vm.account?.name :
                vm.currentStudent?.name
        },
        disabled(vm = this) {
            if (!vm.startDate || !vm.endDate) return true
            if (vm.isGroupOption) return !vm.currentGroup
            if (vm.isTeamOption && vm.currentTeam) return false
            if (!vm.currentStudent && !vm.isStudent) return true
            return false
        },
        queryStudent(vm = this) {
            return vm.$route.query?.studentId || null
        },
        collectionName(vm = this) {
            if (vm.isInjury) return 'injuries'
            if (vm.isWellness) return 'assignedRoutines'
            if (vm.isTeamOption || vm.isGroupOption) return 'groupAssignedRoutines'
            return 'assignedRoutines'
        },
        fieldName(vm = this) {
            if (vm.isGroupOption) return 'groupId'
            return vm.isTeamOption ? 'teamId' : 'studentId'
        },
        fieldValue(vm = this) {
            if (vm.isGroupOption) return vm.currentGroup?.id
            return vm.isTeamOption ? vm.currentTeam?.id : vm.studentId
        },
        isGroupOption(vm = this) {
            return vm.currentOption === 'groups'
        },
        dateField(vm = this) {
            return vm.isInjury ? 'date' : 'rawDate.full'
        },
        isTeamOption(vm = this) {
            return vm.currentOption === 'teams' || vm.isWellness
        },
        isStatisticsSection(vm = this) {
            return !vm.isCompetence && !vm.isInjury && !vm.isWellness
        },
        sectionKey(vm = this) {
            if (vm.isInjury) return 'injury'
            if (vm.isWellness) return 'wellness'
            if (vm.isCompetence) return 'competence'
            return 'statistics'
        },
        teamCacheKey(vm = this) {
            // Solo cacheamos el caso equipo-statistics: ahí el fetch trae a todos los
            // alumnos y los charts solo cambian el studentId al elegir uno u otro.
            if (!vm.isTeamOption || !vm.isStatisticsSection) return null
            return `${vm.currentTeam?.id || ''}|${vm.startDate}|${vm.endDate}`
        },
        subgroups(vm = this) {
            return vm.currentTeam?.groups || []
        },
        subgroupStudentIds(vm = this) {
            if (!vm.isTeamOption || !vm.isStatisticsSection || !vm.currentSubgroup) return null
            const ids = (vm.currentSubgroup.students || []).filter(Boolean)
            return ids.length ? ids : null
        },
        studentTeam(vm = this) {
            const teamId = vm.currentStudent?.team?.id
            if (!teamId) return null
            return vm.teams.find(team => team.id === teamId) || null
        },
        studentSubgroup(vm = this) {
            const subgroupId = vm.currentStudent?.team?.subgroupId
            if (subgroupId == null || !vm.studentTeam) return null
            return (vm.studentTeam.groups || []).find(group => group.id === subgroupId) || null
        },
        showCompareSelect(vm = this) {
            if (vm.isStudent || !vm.isStatisticsSection) return false
            if (vm.isTeamOption || vm.isGroupOption) return false
            return !!vm.studentTeam
        },
        compareOptions(vm = this) {
            const options = [
                { name: vm.$t('statistics.no_compare'), value: 'none' },
                { name: vm.$t('statistics.vs_team'), value: 'team' },
            ]
            if (vm.studentSubgroup) options.push({ name: vm.$t('statistics.vs_subgroup'), value: 'subgroup' })
            return options
        },
        isComparingStudent(vm = this) {
            return vm.showCompareSelect && vm.compareMode !== 'none'
        },
        mainScopeLabel(vm = this) {
            if (vm.subgroupStudentIds) return vm.currentSubgroup?.name || vm.$t('common.subgroup')
            return vm.currentStudent?.name || vm.$t('common.student')
        },
        compareScopeLabel(vm = this) {
            if (vm.subgroupStudentIds) return vm.currentTeam?.name || vm.$t('common.team')
            if (vm.compareMode === 'subgroup') return vm.studentSubgroup?.name || vm.$t('common.subgroup')
            return vm.studentTeam?.name || vm.$t('common.team')
        },
        teamStudents(vm = this) {
            return vm.currentTeam ?
                vm.students.filter(student => student?.team?.id === vm.currentTeam?.id) :
                []
        },
        students(vm = this) {
            if (vm.currentOption === 'students_without_team') {
                return vm.allStudents.filter(s => !s.team)
            }
            return vm.allStudents
        },
        exportName(vm = this) {
            if (vm.isInjury) return vm.$t('common.injuries')
            if (vm.isCompetence) return vm.$t('common.competences')
            if (vm.isWellness) return vm.$t('common.wellness')
            return vm.$t('common.statistics')
        },
        scopeName(vm = this) {
            if (vm.currentStudent?.name) return vm.currentStudent.name
            if (vm.isGroupOption) return vm.currentGroup?.name || ''
            if (vm.isTeamOption) {
                const team = vm.currentTeam?.name || ''
                return vm.currentSubgroup ? `${team} - ${vm.currentSubgroup.name}` : team
            }
            if (vm.isStudent) return vm.account?.name || ''
            return ''
        }
    },
    async mounted() {
        const vm = this
        // console.log('vm', vm)

        vm.startDate = vm.$moment().startOf('month').format('YYYY-MM-DD')
        vm.endDate = vm.$moment().format('YYYY-MM-DD')

        vm.$emit('change:coach', vm.coachId)
        if (!vm.isStudent) await vm.loadStudents()
        if (vm.queryStudent) {
            vm.currentStudent = vm.students.find(student => student.objectID === vm.queryStudent)
            if (vm.currentStudent) vm.loadDocs()
        }
    },
    methods: {
        async loadStudents(force = false) {
            const vm = this
            if (!vm.coachId) { vm.allStudents = []; return }
            if (!force && vm.loadedStudentsCoachId === vm.coachId && vm.allStudents.length > 0) return

            const filter = `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`

            try {
                const { hits } = await UserIndex.search('', { filters: filter, hitsPerPage: 1000 })
                vm.allStudents = hits
                vm.loadedStudentsCoachId = vm.coachId
            } catch (err) {
                console.log(err)
            }
        },
        async loadDocs(preferCache = false) {
            const vm = this

            vm.$emit('change:students', vm.students)

            // Cambio de subgrupo dentro del equipo: los docs cacheados ya traen a TODOS
            // los alumnos, así que reusamos y solo cambia el promedio que derivamos en
            // applyDocs. Emitimos arrays nuevos para que los charts re-rendericen.
            if (preferCache && vm.teamCacheKey && vm.teamCacheKey === vm.cache.key && vm.cache.docs) {
                vm.rawDocs = vm.cache.docs
                vm.rawLastWeeks = vm.cache.lastWeeks
                vm.applyDocs()
                return
            }

            vm.loading = true
            vm.docs = []
            vm.$emit('loading', true)

            try {
                let mainDocs
                let lastWeeks = null
                if (vm.withLastWeeks && vm.isStatisticsSection) {
                    const res = await vm.fetchStatisticsWithLastWeeks()
                    mainDocs = res.mainDocs
                    lastWeeks = res.lastWeeks
                } else {
                    mainDocs = await vm.fetchMainDocs()
                }
                vm.rawDocs = mainDocs
                vm.rawLastWeeks = lastWeeks
                vm.compareData = await vm.fetchCompareDocs()
                vm.applyDocs()

                // El fetch de equipo siempre trae a todos los alumnos, así que el
                // resultado es reusable al cambiar de alumno o volver a promedios.
                if (vm.teamCacheKey) {
                    vm.cache = { key: vm.teamCacheKey, docs: mainDocs, lastWeeks: lastWeeks || [] }
                }
            } catch (err) {
                console.log('loadDocs error', err)
            } finally {
                vm.loading = false
                vm.$emit('loading', false)
            }
        },
        applyDocs() {
            const vm = this
            const raw = vm.rawDocs || []
            const rawLastWeeks = vm.rawLastWeeks
            const ids = vm.subgroupStudentIds

            vm.docs = ids ? getScopeDocs(raw, ids) : raw
            vm.$emit('load', ids ? [...vm.docs] : [...raw])
            vm.$emit('load:compare', ids ? [...raw] : (vm.compareData?.docs || null))

            if (rawLastWeeks !== null) {
                vm.$emit('loadLastWeeks', ids ? getScopeDocs(rawLastWeeks, ids) : [...rawLastWeeks])
                vm.$emit('loadLastWeeks:compare', ids ? [...rawLastWeeks] : (vm.compareData?.lastWeeks || null))
            }

            vm.$emit('change:labels', { main: vm.mainScopeLabel, compare: vm.compareScopeLabel })
        },
        // Comparar desde el scope Alumnos necesita las rutinas del equipo del alumno
        // (las suyas no traen al resto). Se cachea por equipo|modo|rango.
        async fetchCompareDocs() {
            const vm = this
            if (!vm.isComparingStudent) return null

            const teamId = vm.studentTeam?.id
            if (!teamId) return null

            const cacheKey = `${teamId}|${vm.compareMode}|${vm.startDate}|${vm.endDate}`
            if (vm.compareCache.key === cacheKey) return vm.compareCache.data

            try {
                const lwEnd = vm.$moment(vm.endDate).endOf('week')
                const lwStart = vm.$moment(lwEnd).subtract(4, 'weeks').startOf('week')
                const lwStartFull = lwStart.format('YYYY-MM-DD')
                const lwEndFull = lwEnd.format('YYYY-MM-DD')
                const combinedStart = vm.startDate < lwStartFull ? vm.startDate : lwStartFull
                const combinedEnd = vm.endDate > lwEndFull ? vm.endDate : lwEndFull

                const routinesRef = await db.collection('groupAssignedRoutines')
                    .where('teamId', '==', teamId)
                    .where('rawDate.full', '>=', combinedStart)
                    .where('rawDate.full', '<=', combinedEnd)
                    .orderBy('rawDate.full', 'asc').get()

                let advancesById = null
                try {
                    advancesById = await fetchTeamAdvances({ teamId, start: combinedStart, end: combinedEnd })
                } catch (e) {
                    advancesById = null
                }

                let allDocs = await uniqueDayTeamRoutine(routinesRef.docs, vm.students, true, advancesById)
                if (vm.compareMode === 'subgroup') {
                    const ids = (vm.studentSubgroup?.students || []).filter(Boolean)
                    allDocs = ids.length ? getScopeDocs(allDocs, ids) : []
                }

                const within = (doc, from, to) => {
                    const date = doc?.rawDate?.full
                    return !!date && date >= from && date <= to
                }
                const data = {
                    docs: allDocs.filter(doc => within(doc, vm.startDate, vm.endDate)),
                    lastWeeks: allDocs.filter(doc => within(doc, lwStartFull, lwEndFull)),
                }
                vm.compareCache = { key: cacheKey, data }
                return data
            } catch (err) {
                console.log('fetchCompareDocs error', err)
                return null
            }
        },
        async reloadCompare() {
            const vm = this
            vm.compareData = await vm.fetchCompareDocs()
            vm.applyDocs()
        },
        async fetchMainDocs() {
            const vm = this

            if (vm.isInjury || vm.isWellness) {
                let studentIds = []
                if (vm.isGroupOption) {
                    studentIds = (vm.currentGroup?.students || [])
                        .map(s => typeof s === 'string' ? s : (s?.id || s?.objectID))
                        .filter(Boolean)
                } else {
                    vm.currentTeam?.groups.forEach(group => {
                        group.students.forEach(studentId => {
                            if (!studentIds.includes(studentId)) studentIds.push(studentId)
                        })
                    })
                }

                const scopeStudents = vm.isGroupOption
                    ? vm.students.filter(s => studentIds.includes(s.objectID))
                    : vm.teamStudents
                vm.$emit('change:teamStudents', scopeStudents)

                if (vm.isWellness) {
                    if (!studentIds.length) return []
                    const advances = await fetchGroupAdvances({
                        teamId: vm.isGroupOption ? null : vm.currentTeam?.id || null,
                        groupId: vm.isGroupOption ? vm.currentGroup?.id || null : null,
                        start: vm.startDate,
                        end: vm.endDate,
                    })
                    const allowedIds = new Set(studentIds)
                    return advances.filter(a => allowedIds.has(a.studentId)).map(a => ({
                        id: a.id,
                        studentId: a.studentId,
                        rawDate: a.rawDate,
                        routineNumber: a.routineNumber || 1,
                        advance: a.advance,
                    }))
                }

                const chunkSize = 10
                const chunks = []
                for (let i = 0; i < studentIds.length; i += chunkSize) {
                    chunks.push(studentIds.slice(i, i + chunkSize))
                }

                const fetchChunk = async (chunk) => {
                    let query = db.collection(vm.collectionName)
                        .where('studentId', 'in', chunk)

                    if (vm.startDate === vm.endDate) {
                        query = query.where(vm.dateField, '==', vm.startDate)
                    } else {
                        query = query
                            .where(vm.dateField, '>=', vm.startDate)
                            .where(vm.dateField, '<=', vm.endDate)
                    }

                    const collectionRef = await query.orderBy(vm.dateField, 'asc').get()
                    return collectionRef.docs.map(doc => ({ ...doc.data(), id: doc.id }))
                }

                const results = await Promise.all(chunks.map(fetchChunk))
                return results.flat()
            }

            if (vm.fieldValue == null) return []

            let query = db.collection(vm.collectionName)
                .where(vm.fieldName, '==', vm.fieldValue)

            if (vm.isCompetence) {
                query = query.where('type', '==', 'competence')
            }

            if (vm.startDate === vm.endDate) {
                query = query.where(vm.dateField, '==', vm.startDate)
            } else {
                query = query
                    .where(vm.dateField, '>=', vm.startDate)
                    .where(vm.dateField, '<=', vm.endDate)
            }

            const collectionRef = await query.orderBy(vm.dateField, 'asc').get()

            if (vm.isTeamOption || vm.isGroupOption) {
                const advancesById = await vm.loadTeamAdvances(vm.startDate, vm.endDate, vm.isCompetence)
                return await uniqueDayTeamRoutine(collectionRef.docs, vm.students, true, advancesById)
            }

            return await vm.mergeRoutineDocs(collectionRef.docs)
        },
        async loadTeamAdvances(start, end, competence = false) {
            const vm = this
            try {
                return await fetchTeamAdvances({
                    teamId: vm.isTeamOption ? vm.fieldValue : null,
                    groupId: vm.isGroupOption ? vm.fieldValue : null,
                    start, end, competence,
                })
            } catch (e) {
                return null
            }
        },
        async fetchStatisticsWithLastWeeks() {
            const vm = this
            if (vm.fieldValue == null) return { mainDocs: [], lastWeeks: [] }

            const lwEnd = vm.$moment(vm.endDate).endOf('week')
            const lwStart = vm.$moment(lwEnd).subtract(4, 'weeks').startOf('week')
            const lwStartFull = lwStart.format('YYYY-MM-DD')
            const lwEndFull = lwEnd.format('YYYY-MM-DD')

            const combinedStart = vm.startDate < lwStartFull ? vm.startDate : lwStartFull
            const combinedEnd = vm.endDate > lwEndFull ? vm.endDate : lwEndFull

            const routinesRef = await db.collection(vm.collectionName)
                .where(vm.fieldName, '==', vm.fieldValue)
                .where('rawDate.full', '>=', combinedStart)
                .where('rawDate.full', '<=', combinedEnd)
                .orderBy('rawDate.full', 'asc').get()

            let allDocs
            if (vm.isTeamOption || vm.isGroupOption) {
                const advancesById = await vm.loadTeamAdvances(combinedStart, combinedEnd, false)
                allDocs = await uniqueDayTeamRoutine(routinesRef.docs, vm.students, true, advancesById)
            } else {
                allDocs = await vm.mergeRoutineDocs(routinesRef.docs)
            }

            const within = (doc, from, to) => {
                const d = doc && doc.rawDate && doc.rawDate.full
                return !!d && d >= from && d <= to
            }
            return {
                mainDocs: allDocs.filter(doc => within(doc, vm.startDate, vm.endDate)),
                lastWeeks: allDocs.filter(doc => within(doc, lwStartFull, lwEndFull)),
            }
        },
        async mergeRoutineDocs(snapshotDocs) {
            const rawDocs = snapshotDocs.map(doc => ({ ...doc.data(), id: doc.id }))
            await getMergedBlocks(rawDocs.flatMap(d => d.blocks || []))
            return uniqueDayRoutine(await Promise.all(
                rawDocs.map(async doc => ({ ...doc, blocks: await getMergedBlocks(doc.blocks || []) }))
            ))
        },
        async exportDocs() {
            const vm = this
            if (!vm.docs.length) return
            vm.loadingExport = true
            try {
                const XLSX = (await import("xlsx")).default
                const rows = vm.isInjury ? vm.injurySheet()
                    : vm.isCompetence ? vm.competenceSheet()
                        : vm.isWellness ? vm.wellnessSheet()
                            : vm.statisticsSheet()
                const workbook = XLSX.utils.book_new()
                const sheet = XLSX.utils.json_to_sheet(rows)
                XLSX.utils.book_append_sheet(workbook, sheet, vm.exportName.slice(0, 31))
                const parts = [vm.exportName]
                if (vm.scopeName) parts.push(vm.scopeName)
                parts.push(vm.$moment().format('YYYY-MM-DD HH-mm'))
                await saveWorkbook(XLSX, workbook, parts.join(' - '), vm.$t('common.export'))
            } catch (err) {
                console.log('exportDocs error', err)
            } finally {
                vm.loadingExport = false
            }
        },
        sharePeriod() {
            const vm = this
            const end = vm.endDate ? vm.$moment(vm.endDate).format('DD-MM-YYYY') : ''
            if (vm.isWellness) return end
            const start = vm.startDate ? vm.$moment(vm.startDate).format('DD-MM-YYYY') : ''
            if (!start) return end
            return `${start} ${vm.$t('common.date_range_to')} ${end}`
        },
        async shareImage() {
            const vm = this
            if (vm.loadingShare || !vm.docs.length) return
            if (!vm.requirePlan('shareImage')) return
            const node = document.querySelector('[data-share-target="statistics"]')
            if (!node) return
            vm.loadingShare = true
            try {
                await shareNodeAsImage(node, {
                    title: vm.exportName,
                    period: vm.sharePeriod(),
                    subject: vm.scopeName,
                    isDark: vm.$vuetify.theme.dark,
                    filenameParts: [vm.exportName, vm.sharePeriod(), vm.scopeName],
                    dialogTitle: vm.$t('common.share_image'),
                    loadingText: vm.$t('common.generating_image'),
                })
            } catch (err) {
                console.log('shareImage error', err)
            } finally {
                vm.loadingShare = false
            }
        },
        studentById(id) {
            return this.allStudents.find(s => s.objectID === id) || null
        },
        studentNameById(id) {
            const student = this.studentById(id)
            return student?.name || student?.email || ''
        },
        studentEmailById(id) {
            return this.studentById(id)?.email || ''
        },
        formatDate(value) {
            const vm = this
            if (!value) return ''
            if (typeof value === 'object' && typeof value.toDate === 'function') return vm.$moment(value.toDate()).format('DD-MM-YYYY')
            const m = vm.$moment(value, ['YYYY-MM-DD', 'DD-MM-YYYY', vm.$moment.ISO_8601], true)
            return m.isValid() ? m.format('DD-MM-YYYY') : String(value)
        },
        wellnessSheet() {
            const vm = this
            const multi = vm.isTeamOption || vm.isGroupOption
            return vm.docs.filter(doc => doc?.advance?.pre).map(doc => {
                const p = doc?.advance?.preInfo || {}
                const row = { [vm.$t('common.date')]: doc.rawDate?.full || '' }
                if (multi) {
                    row[vm.$t('common.student')] = vm.studentNameById(doc.studentId)
                    row[vm.$t('common.email')] = vm.studentEmailById(doc.studentId)
                }
                row[vm.$t('statistics.sleep_night')] = vm.safeNumber(p.sleep_night)
                row[vm.$t('statistics.muscles_hurt')] = vm.safeNumber(p.muscles_hurt)
                row[vm.$t('statistics.tired')] = vm.safeNumber(p.tired)
                row[vm.$t('statistics.stressed')] = vm.safeNumber(p.stressed)
                row[vm.$t('statistics.energy')] = vm.safeNumber(p.energy)
                row[vm.$t('statistics.wellness')] = vm.safeNumber(sumWellnessPreInfo(p))
                return row
            })
        },
        competenceSheet() {
            const vm = this
            return vm.docs.map(doc => {
                const post = doc?.advance?.postInfo || {}
                return {
                    [vm.$t('common.date')]: doc.rawDate?.full || '',
                    [vm.$t('statistics.rival')]: doc.versus || '',
                    [vm.$t('statistics.played')]: vm.safeNumber(post.duration),
                    [vm.$tc('common.minutes', 2)]: vm.safeNumber(doc.minutes),
                    [vm.$t('statistics.effort')]: doc?.advance?.post ? vm.safeNumber(post.effort) : '',
                    [vm.$t('statistics.wellness')]: doc?.advance?.pre ? vm.safeNumber(sumWellnessPreInfo(doc.advance.preInfo)) : '',
                    [vm.$t('common.position')]: post.position || '',
                }
            })
        },
        injurySheet() {
            const vm = this
            return vm.docs.map(doc => ({
                [vm.$t('common.student')]: vm.studentNameById(doc.studentId),
                [vm.$t('common.email')]: vm.studentEmailById(doc.studentId),
                [vm.$t('common.condition')]: vm.injuryComplexityLabel(doc.complexity),
                [vm.$t('common.injury')]: vm.injuryTypeLabel(doc.type),
                [vm.$t('common.zone')]: vm.injuryZoneLabel(doc.zone),
                [vm.$t('common.date')]: vm.formatDate(doc.date),
                [vm.$t('common.discharge')]: vm.formatDate(doc.dischargeDate),
                [vm.$t('common.moment')]: vm.injuryMomentLabel(doc.wasIt),
                [vm.$t('common.observation')]: doc.comment || '',
            }))
        },
        statisticsSheet() {
            const vm = this
            return vm.docs.map(doc => {
                const preInfo = doc?.advance?.preInfo || null
                const wellness = calculateWellnessPreInfo(preInfo)
                const postInfo = doc?.advance?.postInfo || null
                const duration = vm.safeNumber(postInfo?.duration)
                const effort = vm.safeNumber(postInfo?.effort)
                const charge = duration * effort
                const minutes = doc.type === 'competence' ? duration : 0

                let sets_total = 0
                let sets_strength = 0
                let sets_speed = 0
                let sets_resistance = 0
                let percentage = 0
                let rpe = 0
                let rir = 0
                let tonnage = 0
                let distance = 0
                let quantity_strong = 0
                doc.blocks?.forEach(block => {
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest') {
                            let sets = Number(exercise.config?.sets?.length || 0)
                            sets_total += sets
                            if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.STRONG) sets_strength += sets
                            if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.SPEED) sets_speed += sets
                            if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.RESISTANCE) sets_resistance += sets

                            exercise.config?.sets?.forEach((set, i) => {
                                if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.STRONG) {
                                    let reps = vm.takeValueFromSet(set, 'reps') || 0
                                    tonnage += (vm.takeValueFromSet(set, 'weight') * reps)
                                    let valuePercentage = vm.takeValueFromSet(set, 'percentage')
                                    percentage += valuePercentage
                                    let valueRpe = vm.takeValueFromSet(set, 'rpe')
                                    rpe += valueRpe ? vm.getValueFromTable(valueRpe, set, 'rpeTable') : 0
                                    let valueRir = vm.takeValueFromSet(set, 'rir')
                                    rir += valueRir ? vm.getValueFromTable(valueRir, set, 'rirTable') : 0
                                    if (valuePercentage > 0 || valueRir > 0 || valueRpe > 0) quantity_strong++
                                } else if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.SPEED || vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.RESISTANCE) {
                                    distance += (vm.takeValueFromSet(set, 'distance') * sets)
                                }
                            })
                        }
                    })
                })

                let intensity_strength = percentage + rpe + rir
                if (quantity_strong > 0) intensity_strength /= quantity_strong

                return {
                    [vm.$t('common.date')]: doc.rawDate?.full || '',
                    [vm.$t('statistics.wellness')]: vm.safeNumber(wellness),
                    [vm.$t('common.duration')]: vm.safeNumber(duration),
                    [vm.$t('statistics.effort')]: vm.safeNumber(effort),
                    [vm.$t('statistics.charge')]: vm.safeNumber(charge),
                    [vm.$t('statistics.sets_total')]: vm.safeNumber(sets_total),
                    [vm.$t('statistics.sets_strength')]: vm.safeNumber(sets_strength),
                    [vm.$t('statistics.sets_speed')]: vm.safeNumber(sets_speed),
                    [vm.$t('statistics.sets_resistance')]: vm.safeNumber(sets_resistance),
                    [vm.$t('statistics.intensity_strength')]: vm.safeNumber(Number(intensity_strength).toFixed(1)),
                    [vm.$t('statistics.tonnage')]: vm.safeNumber(tonnage),
                    [vm.$t('statistics.distance')]: vm.safeNumber(distance),
                    [vm.$t('statistics.minutes')]: vm.safeNumber(minutes),
                }
            })
        },
        reset() {
            const vm = this
            if (vm.disabled) vm.$emit('reset')
        },
        // Cambiar de scope o de seccion arranca de cero: los selectores de la barra y
        // los datos en pantalla son de la seleccion anterior.
        resetSelection() {
            const vm = this
            vm.currentStudent = null
            vm.currentTeam = null
            vm.currentGroup = null
            vm.currentSubgroup = null
            vm.compareMode = 'none'
            vm.docs = []
            vm.rawDocs = []
            vm.rawLastWeeks = null
            vm.compareData = null
            vm.compareCache = { key: null, data: null }
            vm.cache = { key: null, docs: null, lastWeeks: null }
            vm.$emit('reset')
        },
        onWellnessDate(value) {
            const vm = this
            if (!value) return
            vm.endDate = value
            vm.startDate = vm.$moment(value).subtract(3, 'weeks').format('YYYY-MM-DD')
        },
        safeNumber(value) {
            const num = Number(value)
            return Number.isFinite(num) ? num : 0
        },
        takeValueFromSet(set, attribute) {
            const vm = this
            const value = set?.data?.find(data => data.short_name === `short_${attribute}`)
            return vm.safeNumber(value?.value)
        },
        getValueFromTable(value, set, typeTable) {
            const vm = this
            if (typeTable === 'rpeTable') {
                if (value > 10) value = 10
                if (value < 7) value = 7
            }
            if (typeTable === 'rirTable') {
                if (value > 4) value = 4
                if (value < 0) value = 0
            }
            let findValueTable = vm[typeTable]?.find(table => table.name == value) || null
            let reps = set.data.find(data => data.short_name === 'short_reps') || null
            let repsValue = reps && (Number(reps.value || 0) - 1) || 0
            if (repsValue > 7) repsValue = 7
            if (repsValue < 0) repsValue = 0
            return findValueTable && repsValue && findValueTable.reps[repsValue] || 0
        },
    },
    watch: {
        currentCoach(val) {
            this.reset()
            if (val?.objectID) this.loadStudents()
        },
        currentStudent() {
            const vm = this
            vm.reset()
            if (vm.compareMode === 'subgroup' && !vm.studentSubgroup) vm.compareMode = 'none'
            // Cambio (o limpieza) de alumno con datos ya cargados: reusar cache.
            if (vm.teamCacheKey && vm.teamCacheKey === vm.cache.key) vm.loadDocs(true)
        },
        startDate(val) {
            this.reset()
            this.$emit('change:date:start', val)
        },
        endDate(val) {
            this.reset()
            this.$emit('change:date:end', val)
        },
        studentId(val) {
            this.$emit('change:student', val)
            this.$emit('change:studentName', this.studentName)
        },
        students(val) {
            this.$emit('change:students', val)
        },
        coachId(val) {
            const vm = this
            vm.$emit('change:coach', val)
            vm.currentStudent = null
            vm.currentTeam = null
            vm.currentGroup = null
            vm.reset()
            vm.loadStudents()
        },
        currentOption() {
            const vm = this
            vm.loadStudents()
            vm.resetSelection()
        },
        sectionKey() {
            this.resetSelection()
        },
        currentTeam(val) {
            this.$emit('change:teamId', val?.id || null)
            this.currentStudent = null
            this.currentSubgroup = null
        },
        currentSubgroup() {
            const vm = this
            if (vm.teamCacheKey && vm.teamCacheKey === vm.cache.key) vm.applyDocs()
        },
        compareMode() {
            const vm = this
            if (vm.rawDocs.length) vm.reloadCompare()
        },
        currentGroup() {
            this.reset()
        },
        isWellness(val) {
            const vm = this
            const base = vm.endDate || vm.$moment().format('YYYY-MM-DD')
            vm.endDate = base
            vm.startDate = val ?
                vm.$moment(base).subtract(3, 'weeks').format('YYYY-MM-DD') :
                vm.$moment(base).startOf('month').format('YYYY-MM-DD')
        }
    }
}
</script>

<style scoped>
.filters-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 24px;
    border-bottom: 1px solid var(--v-outline-base);
}

.filters-sep {
    width: 1px;
    height: 24px;
    background: var(--v-outline-base);
}

.filter-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.filter-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
}

.filter-control {
    width: 168px;
}

.filter-control--wide {
    width: 190px;
}

.filter-control--date {
    width: 132px;
}

.filter-control ::v-deep .v-text-field {
    margin: 0;
}

.filter-control ::v-deep .v-text-field--solo input,
.filter-control ::v-deep .v-text-field--solo .v-select__selection {
    font-size: 13px;
}

.load-btn.v-btn {
    height: 38px;
    min-width: 0;
    border-radius: 8px;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 600;
    padding: 0 18px;
    box-shadow: none !important;
    background: linear-gradient(135deg, rgba(204, 255, 51, 0.15), rgba(51, 153, 153, 0.15)) !important;
    border: 1px solid rgba(204, 255, 51, 0.3);
    color: var(--v-primary-base) !important;
}

.theme--light .load-btn.v-btn {
    background: linear-gradient(135deg, rgba(51, 153, 153, 0.12), rgba(102, 204, 204, 0.12)) !important;
    border-color: rgba(51, 153, 153, 0.4);
    color: var(--v-accent-base) !important;
}

.export-btn.v-btn {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    border: 1px solid var(--v-outline2-base);
    background: var(--v-surface2-base) !important;
    color: var(--v-txtPrimary-base) !important;
}

.export-btn.v-btn:hover {
    border-color: var(--v-accent-base);
}

.export-btn.v-btn.v-btn--disabled {
    border-color: var(--v-outline-base);
    color: var(--v-txtMuted-base) !important;
    opacity: .5;
}

@media (max-width: 760px) {
    .filters-bar {
        padding: 12px 16px;
    }

    .filters-sep {
        display: none;
    }

    .filter-group {
        flex: 1 1 180px;
    }

    .filter-control,
    .filter-control--wide,
    .filter-control--date {
        flex: 1 1 auto;
        width: auto;
    }

    .load-btn.v-btn {
        flex: 1 1 100%;
    }

    .ml-auto {
        margin-left: 0 !important;
    }
}
</style>